import React, { useCallback, useEffect, useRef, useState } from "react";
import DepthSnapshot from "./HomeDepthSnapshots";
import HomeTransportStage from "./HomeTransportStage";

function ClipVideo({ src, label }) {
  const ref = useRef(null);

  useEffect(() => {
    const video = ref.current;
    if (!video) return undefined;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      video.pause();
      return undefined;
    }
    const play = () => video.play().catch(() => {});
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) play();
          else video.pause();
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <video
      ref={ref}
      className="hs-media-clip"
      muted
      loop
      playsInline
      preload="metadata"
      aria-label={label}
    >
      <source src={src} type="video/mp4" />
    </video>
  );
}

const BUCKETS = [
  {
    id: "in",
    title: "In catchment",
    text: "You’re inside this school’s priority area — usually your strongest chance.",
    tone: "green",
  },
  {
    id: "open",
    title: "Open to all",
    text: "Anyone can apply. Exam score usually decides who gets in.",
    tone: "indigo",
  },
  {
    id: "possible",
    title: "Possible",
    text: "Close enough that a place might still reach you — worth a closer look.",
    tone: "slate",
  },
  {
    id: "out",
    title: "Outside",
    text: "Nearby, but outside the priority area — still useful to know for the shortlist or move.",
    tone: "amber",
  },
];

const FEATURE_SPOTLIGHTS = [
  {
    id: "rank",
    kicker: "Ranking",
    title: "Sort all 163 grammars your way",
    lead: "GCSE, A level, Ofsted, or miles from your pin — the live ranking table parents use to shortlist.",
    cue: "GCSE · A level · Ofsted · miles",
    page: "ranking",
    cta: "Open ranking",
    graphic: "rank",
    tone: "teal",
  },
  {
    id: "compare",
    kicker: "Compare",
    title: "Line up schools on the same figures",
    lead: "Put Tiffin Girls next to Nonsuch and Wallington Girls — results, Ofsted, and destinations in one view.",
    cue: "Same figures · clear trade-offs",
    page: "compare",
    cta: "Open compare",
    graphic: "compare",
    tone: "blue",
  },
  {
    id: "deadlines",
    kicker: "Exam dates & deadlines",
    title: "Know every exam date — and don’t miss a deadline",
    lead: "Registration, stage one, stage two, and the CAF deadline for each school — in one place, so nothing slips past.",
    cue: "Register · exams · CAF · offer day",
    page: "map",
    cta: "See key dates on the map",
    graphic: "deadlines",
    tone: "slate",
  },
];

const DEPTH = [
  {
    id: "panel",
    title: "Catchment panel",
    eyebrow: "On the map",
    blurb: "Turn on Bucks, Reading, and Kendrick — watch catchments stack and overlap.",
    explain:
      "Click a few Buckinghamshire grammars plus Reading School and Kendrick. The map draws each priority area so you can see where they stack and where they diverge.",
    page: "map",
    cue: "Bucks · Reading · Kendrick · overlap",
    graphic: "panel",
    accent: "teal",
  },
  {
    id: "alloc",
    title: "Allocation details",
    eyebrow: "Offers",
    blurb: "Year-by-year how far offers reached — Designated Area or beyond.",
    explain:
      "Recent offer years sit in the school file: whether places stayed inside the Designated Area, and how far the last score filled. Context for how tight things were — not a forecast.",
    page: "map",
    cue: "Recent offers · how far they reached",
    graphic: "alloc",
    accent: "emerald",
  },
  {
    id: "oxbridge",
    title: "Oxbridge offers",
    eyebrow: "Destinations",
    blurb: "Published Oxbridge counts and higher-education leavers %.",
    explain:
      "Where a school publishes Oxbridge offer counts or DfE destinations, they appear beside GCSE and A level — useful sixth-form context, never invented.",
    page: "map",
    cue: "Published offer counts · leavers",
    graphic: "oxbridge",
    accent: "blue",
  },
  {
    id: "exams",
    title: "Exam details",
    eyebrow: "Year 7 entry",
    blurb: "Stage one, stage two, register-by dates, and subjects.",
    explain:
      "Stage one, stage two, register-by dates, and what the papers cover for this school — including shared tests with neighbours — without hunting the PDF first.",
    page: "map",
    cue: "Papers · dates · register",
    graphic: "exams",
    accent: "coral",
  },
  {
    id: "guide",
    title: "School guide",
    eyebrow: "Year 7 path",
    blurb: "Catchment → apply → exams → place order → offer day.",
    explain:
      "One calm path per school: catchment layers, how to apply, exams, place order, and offer day — written for parents.",
    page: "map",
    cue: "Catchment → apply → exams → offer day",
    graphic: "guide",
    accent: "gold",
  },
];

function useReveal(selector) {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;
    const nodes = [...root.querySelectorAll(selector)];
    nodes.forEach((node) => node.classList.add("hs-wait"));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("is-in");
        });
      },
      { threshold: 0.16, rootMargin: "0px 0px -6% 0px" }
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [selector]);

  return rootRef;
}

function FeatureSpotlight({ item, onOpenPage }) {
  return (
    <section className={`hs-spotlight hs-spotlight--stack hs-reveal hs-spotlight--${item.tone}`} aria-label={item.kicker}>
      <div className="hs-spotlight-shot is-active">
        <DepthSnapshot kind={item.graphic} />
      </div>
      <div className="hs-spotlight-copy">
        <p className="hs-kicker">{item.kicker}</p>
        <h2 className="hs-title hs-title--sm">{item.title}</h2>
        <p className="hs-lead">{item.lead}</p>
        <div className="hs-spotlight-actions">
          <span className="hs-spotlight-cue">{item.cue}</span>
          <button type="button" className="hs-spotlight-cta" onClick={() => onOpenPage?.(item.page)}>
            {item.cta}
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </section>
  );
}

function DepthExplorer({ onOpenPage }) {
  const [active, setActive] = useState(0);
  const trackRef = useRef(null);
  const syncingRef = useRef(false);

  const scrollToCard = useCallback((index, behavior = "smooth") => {
    const track = trackRef.current;
    if (!track) return;
    const next = Math.max(0, Math.min(index, DEPTH.length - 1));
    syncingRef.current = true;
    setActive(next);
    track.scrollTo({ left: next * track.clientWidth, behavior });
    window.setTimeout(() => {
      syncingRef.current = false;
    }, 420);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    const onScroll = () => {
      if (syncingRef.current) return;
      const width = track.clientWidth || 1;
      const next = Math.round(track.scrollLeft / width);
      setActive(Math.max(0, Math.min(next, DEPTH.length - 1)));
    };

    track.addEventListener("scroll", onScroll, { passive: true });
    const onResize = () => scrollToCard(active, "auto");
    window.addEventListener("resize", onResize);
    return () => {
      track.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [active, scrollToCard]);

  const item = DEPTH[active];

  return (
    <div className="hs-depth-shell hs-depth-shell--stage">
      <div className="hs-depth-main">
        <div className="hs-depth-track" ref={trackRef}>
          {DEPTH.map((entry, index) => (
            <article
              key={entry.id}
              className={`hs-depth-card hs-depth-card--poster${index === active ? " is-active" : ""}`}
            >
              <div className="hs-depth-shot">
                <DepthSnapshot kind={entry.graphic} />
              </div>
              <div className="hs-depth-card-copy">
                <p className="hs-depth-card-eyebrow">{entry.eyebrow}</p>
                <h3>{entry.title}</h3>
                <p>{entry.blurb}</p>
                <span className="hs-depth-card-cue">{entry.cue}</span>
              </div>
            </article>
          ))}
        </div>

        <div className="hs-depth-controls">
          <button
            type="button"
            className="hs-depth-arrow"
            aria-label="Previous card"
            disabled={active <= 0}
            onClick={() => scrollToCard(active - 1)}
          >
            ←
          </button>
          <div className="hs-depth-dots" role="tablist" aria-label="Feature cards">
            {DEPTH.map((entry, index) => (
              <button
                key={entry.id}
                type="button"
                role="tab"
                aria-label={entry.title}
                aria-selected={index === active}
                className={index === active ? "is-active" : undefined}
                onClick={() => scrollToCard(index)}
              />
            ))}
          </div>
          <button
            type="button"
            className="hs-depth-arrow"
            aria-label="Next card"
            disabled={active >= DEPTH.length - 1}
            onClick={() => scrollToCard(active + 1)}
          >
            →
          </button>
        </div>

        <div className="hs-depth-foot">
          <p className="hs-depth-explain">{item.explain}</p>
          <button type="button" className="hs-depth-cta" onClick={() => onOpenPage?.(item.page)}>
            Open {item.title.toLowerCase()}
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function HomeStory({ onOpenPage }) {
  const rootRef = useReveal(".hs-reveal");

  return (
    <div className="hs" ref={rootRef}>
      <section className="hs-stage hs-reveal" aria-label="Exact catchments">
        <div className="hs-stage-copy">
          <p className="hs-kicker hs-kicker--teal">Exact catchments</p>
          <h2 className="hs-title">
            Catchment areas and distance circles drawn from each school&apos;s published map — not a
            rough guess.
          </h2>
          <p className="hs-lead">
            Set a home, then stack schools and see where the areas meet. Other sites rarely show
            catchments this accurately. SchoolCatch does — so you can aim for the best possible area
            when you move or choose.
          </p>
          <button type="button" className="hs-link" onClick={() => onOpenPage?.("map")}>
            Open the catchment map
            <span aria-hidden="true">→</span>
          </button>
        </div>

        <div className="hs-stage-clips" aria-label="How it looks on the map">
          <figure className="hs-media">
            <ClipVideo
              src="/home-clips/set-home.mp4?v=14"
              label="Enter a postcode or place a pin on the map"
            />
            <figcaption>Enter a postcode — or drop a pin on the map</figcaption>
          </figure>
          <figure className="hs-media">
            <img
              className="hs-media-clip"
              src="/home-clips/catchment-overlap.gif?v=2"
              alt="Tiffin Girls’, Wallington Girls’, and Nonsuch catchments overlapping on the map"
              loading="lazy"
            />
            <figcaption>Stack catchments and see the overlap</figcaption>
          </figure>
        </div>
      </section>

      <section className="hs-band hs-reveal" aria-label="Where you stand">
        <div className="hs-band-head">
          <p className="hs-kicker hs-kicker--emerald">Near your home</p>
          <h2 className="hs-title hs-title--sm">Know the schools near you — and your catchment chances</h2>
          <p className="hs-lead">
            After you set a home, nearby grammars land in four clear groups. Same list as the map —
            so you can see standing at a glance.
          </p>
        </div>
        <ol className="hs-buckets">
          {BUCKETS.map((bucket, index) => (
            <li key={bucket.id} className={`hs-bucket hs-bucket--${bucket.tone}`}>
              <span className="hs-bucket-index">{String(index + 1).padStart(2, "0")}</span>
              <strong>{bucket.title}</strong>
              <span>{bucket.text}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="hs-journey hs-reveal" aria-label="Transport">
        <div className="hs-journey-copy">
          <p className="hs-kicker hs-kicker--blue">The journey</p>
          <h2 className="hs-title hs-title--sm">How the school run looks from your pin</h2>
          <p className="hs-lead">
            Open a school and see bus and train options on the map from the home you just set — then
            walk or drive if that fits better.
          </p>
          <button type="button" className="hs-link" onClick={() => onOpenPage?.("map")}>
            Go to map
            <span aria-hidden="true">→</span>
          </button>
        </div>
        <HomeTransportStage />
      </section>

      {FEATURE_SPOTLIGHTS.map((item) => (
        <FeatureSpotlight key={item.id} item={item} onOpenPage={onOpenPage} />
      ))}

      <section className="hs-depth hs-reveal" aria-label="Deeper tools">
        <div className="hs-band-head">
          <p className="hs-kicker hs-kicker--gold">Then go deeper</p>
          <h2 className="hs-title hs-title--sm">Catchment detail, allocation, and the school file</h2>
          <p className="hs-lead">
            Use the arrows — or swipe — to move through catchment, offers, Oxbridge, exams, and the
            school guide.
          </p>
        </div>
        <DepthExplorer onOpenPage={onOpenPage} />
      </section>

      <section className="hs-close hs-reveal">
        <h2 className="hs-close-title">Start with a home</h2>
        <p className="hs-close-text">
          Enter a postcode above, or open the map and place the pin where you live — or where you
          might move.
        </p>
        <button type="button" className="hs-close-cta" onClick={() => onOpenPage?.("map")}>
          Open the map
        </button>
      </section>
    </div>
  );
}
